<template>
  <component
    :is="props.elementType"
    ref="buttonRef"
    :class="buttonClasses"
    :type="props.elementType === 'button' ? buttonType : null"
    :href="props.elementType === 'a' ? href : null"
    :target="props.elementType === 'a' ? target : null"
    :rel="props.elementType === 'a' && target === '_blank' ? rel || 'noopener noreferrer' : rel"
    :disabled="props.elementType === 'button' && (disabled || loading)"
    :aria-disabled="disabled || loading"
    :aria-busy="loading"
    @click="handleClick"
    @keydown="handleKeydown"
    @pointerenter="handlePointerEnter"
    @pointerleave="handlePointerLeave"
    @pointermove="handlePointerMove"
    v-bind="$attrs"
  >
    <div
      class="btn-glow absolute inset-0 opacity-0 scale-95 pointer-events-none z-10 transition-all duration-300 ease-out"
      :style="glowStyle"
    ></div>

    <div :class="contentClasses">
      <!-- Loading state -->
      <div
        v-if="loading"
        class="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"
      ></div>

      <!-- Icon before text -->
      <slot v-if="!loading && iconPosition === 'left'" name="icon"></slot>

      <!-- Text content -->
      <span class="btn__text relative z-30 transition-colors duration-300 ease-out">
        <slot>{{ text }}</slot>
      </span>

      <!-- Icon after text -->
      <slot v-if="!loading && iconPosition === 'right'" name="icon"></slot>
    </div>
  </component>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { gsap } from 'gsap'

defineOptions({
  name: 'MagicButton',
  inheritAttrs: false,
})

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'light', 'flat'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value),
  },
  text: {
    type: String,
    default: 'Click me',
  },
  elementType: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'a'].includes(value),
  },
  buttonType: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value),
  },
  href: {
    type: String,
    default: null,
  },
  target: {
    type: String,
    default: null,
    validator: (value) => value === null || ['_blank', '_self', '_parent', '_top'].includes(value),
  },
  rel: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  iconPosition: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value),
  },
})

const emit = defineEmits(['click'])

const buttonRef = ref(null)
let gsapContext = null
let sparkContainer = null
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

const buttonClasses = computed(() => {
  const baseClasses = [
    'magic-btn',
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'font-semibold',
    'cursor-pointer',
    'border-none',
    'relative',
    'overflow-hidden',
    'transition-all',
    'duration-300',
    'ease-out',
    'transform-gpu',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-purple-500',
    'focus-visible:ring-offset-2',
    'focus-visible:ring-offset-gray-900',
    'no-underline', // Para enlaces
  ]

  // Size classes
  const sizeClasses = {
    sm: ['px-3', 'py-2', 'text-sm', 'rounded-lg'],
    md: ['px-6', 'py-3', 'text-base', 'rounded-xl'],
    lg: ['px-8', 'py-4', 'text-lg', 'rounded-xl'],
    xl: ['px-10', 'py-5', 'text-xl', 'rounded-2xl'],
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-gray-900',
      'bg-gradient-to-br',
      'from-[--color-electric-purple]',
      'to-gray-300',
      'shadow-lg',
      'shadow-[--color-electric-purple]/20',
      'hover:text-white',
      'hover:shadow-xl',
      'hover:shadow-[--color-electric-purple]/40',
    ],
    secondary: [
      'border',
      'border-[--color-steel]/40',
      'bg-gradient-to-br',
      'from-[--color-charcoal]/80',
      'to-[--color-deep-black]/90',
      'text-[--color-ash]',
      'backdrop-blur-sm',
      'shadow-lg',
      'shadow-[--color-charcoal]/30',
      'hover:border-[--color-ash]',
      'hover:text-[--color-ash]',
      'hover:shadow-xl',
      'hover:shadow-[--color-steel]/50',
      'hover:from-[--color-charcoal]/90',
      'hover:to-[--color-deep-black]',
    ],
    light: [
      'bg-white',
      'text-gray-900',
      'border',
      'border-gray-200',
      'shadow-lg',
      'shadow-gray-200/50',
      'hover:text-gray-700',
      'hover:shadow-xl',
      'hover:shadow-gray-300/60',
      'hover:border-gray-300',
    ],
    flat: [
      'bg-[--color-electric-purple]',
      'text-white',
      'shadow-md',
      'shadow-[--color-electric-purple]/20',
      'hover:bg-[--color-electric-purple]/90',
      'hover:shadow-lg',
    ],
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // State classes
  const stateClasses = []
  if (props.disabled || props.loading) {
    stateClasses.push('opacity-60', 'cursor-not-allowed', 'pointer-events-none')
  }

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses,
    ...stateClasses,
  ]
})

const contentClasses = computed(() => [
  'flex',
  'items-center',
  'justify-center',
  'gap-2',
  'relative',
  'z-20',
])

const glowStyle = computed(() => {
  const glowConfigs = {
    primary:
      'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255, 255, 255, 0.3) 0%, rgba(76, 0, 255, 0.2) 25%, transparent 70%)',
    secondary:
      'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(191, 191, 191, 0.3) 0%, rgba(90, 90, 90, 0.2) 30%, rgba(43, 43, 43, 0.1) 60%, transparent 80%)',
    light:
      'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.2) 25%, transparent 70%)',
    flat: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 25%, transparent 70%)',
  }

  return { background: glowConfigs[props.variant] }
})

// Función para obtener el shadow por defecto según la variante
const getDefaultShadow = () => {
  const defaultShadows = {
    primary: '0 8px 25px rgba(76, 0, 255, 0.5), 0 4px 12px rgba(0, 0, 0, 0.2)',
    secondary: '0 8px 25px rgba(43, 43, 43, 0.5), 0 4px 12px rgba(90, 90, 90, 0.3)',
    light: '0 8px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(100, 116, 139, 0.2)',
    flat: '0 4px 12px rgba(76, 0, 255, 0.3)',
  }

  return defaultShadows[props.variant]
}

const handleClick = (event) => {
  if (props.disabled || props.loading) {
    event.preventDefault()
    return
  }

  // Para enlaces, permitir el comportamiento por defecto del navegador
  // Solo prevenir si es necesario para efectos especiales
  if (props.elementType === 'a' && !props.href) {
    event.preventDefault()
  }

  createMagicEffects(event, 'click')
  emit('click', event)
}

const handleKeydown = (event) => {
  if (props.disabled || props.loading) return

  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    createMagicEffects(event, 'keyboard')
    emit('click', event)
  }
}

const handlePointerEnter = (event) => {
  if (props.disabled || props.loading || prefersReducedMotion) return

  if (!gsapContext) return

  gsapContext.add(() => {
    const button = buttonRef.value
    const glowElement = button.querySelector('.btn-glow')

    // Matar cualquier animación previa para reiniciar
    gsap.killTweensOf([button, glowElement])

    gsap
      .timeline()
      .to(button, {
        duration: 0.3,
        y: -3,
        scale: 1.05,
        ease: 'power2.out',
      })
      .to(
        glowElement,
        {
          duration: 0.3,
          opacity: 1,
          scale: 1,
          ease: 'power2.out',
        },
        0
      )
  })
}

const handlePointerLeave = (event) => {
  if (props.disabled || props.loading || prefersReducedMotion) return

  if (!gsapContext) return

  gsapContext.add(() => {
    const button = buttonRef.value
    const glowElement = button.querySelector('.btn-glow')

    // Matar cualquier animación previa para reiniciar
    gsap.killTweensOf([button, glowElement])

    gsap
      .timeline()
      .to(button, {
        duration: 0.3,
        y: 0,
        scale: 1,
        boxShadow: getDefaultShadow(), // Restaurar shadow por defecto
        ease: 'power2.out',
      })
      .to(
        glowElement,
        {
          duration: 0.3,
          opacity: 0,
          scale: 0.95,
          ease: 'power2.out',
        },
        0
      )
  })
}

const handlePointerMove = (event) => {
  if (props.disabled || props.loading || prefersReducedMotion) return

  if (!gsapContext) return

  gsapContext.add(() => {
    const button = buttonRef.value
    const rect = button.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const xPercent = (x / rect.width) * 100
    const yPercent = (y / rect.height) * 100

    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const deltaX = x - centerX
    const deltaY = y - centerY

    const shadowOffsetX = -deltaX * 0.3
    const shadowOffsetY = -deltaY * 0.3

    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY)
    const intensity = Math.min(distance / maxDistance, 1)

    // Configuración de colores por variante
    const shadowConfigs = {
      primary: {
        primary: 'rgba(76, 0, 255, ',
        secondary: 'rgba(0, 0, 0, ',
        glow: 'rgba(76, 0, 255, ',
      },
      secondary: {
        primary: 'rgba(191, 191, 191, ', // ash
        secondary: 'rgba(90, 90, 90, ', // steel
        glow: 'rgba(43, 43, 43, ', // charcoal
      },
      light: {
        primary: 'rgba(0, 0, 0, ',
        secondary: 'rgba(100, 116, 139, ',
        glow: 'rgba(0, 0, 0, ',
      },
      flat: {
        primary: 'rgba(76, 0, 255, ',
        secondary: 'rgba(0, 0, 0, ',
        glow: 'rgba(120, 80, 255, ',
      },
    }

    const colors = shadowConfigs[props.variant]

    gsap.set(button, {
      '--mouse-x': `${xPercent}%`,
      '--mouse-y': `${yPercent}%`,
    })

    gsap.to(button, {
      duration: 0.2,
      boxShadow: `
        ${shadowOffsetX}px ${shadowOffsetY}px ${20 + intensity * 20}px ${colors.primary}${0.4 + intensity * 0.2}),
        ${shadowOffsetX * 0.5}px ${shadowOffsetY * 0.5}px ${15 + intensity * 10}px ${colors.secondary}${0.3 + intensity * 0.2}),
        0 0 ${30 + intensity * 20}px ${colors.glow}${0.1 + intensity * 0.1})
      `,
      ease: 'power2.out',
    })
  })
}

// Función para crear efectos mágicos (chispas y ripple)
function createMagicEffects(event, source = 'click') {
  if (prefersReducedMotion || props.disabled || props.loading) return

  const buttonElement = buttonRef.value
  if (!buttonElement || !gsapContext) return

  let clickX, clickY, relativeX, relativeY

  if (source === 'keyboard') {
    // Para eventos de teclado, usar el centro del botón
    const rect = buttonElement.getBoundingClientRect()
    clickX = rect.left + rect.width / 2
    clickY = rect.top + rect.height / 2
    relativeX = rect.width / 2
    relativeY = rect.height / 2
  } else {
    // Para eventos de pointer/mouse
    const rect = buttonElement.getBoundingClientRect()
    clickX = event.clientX
    clickY = event.clientY
    relativeX = event.clientX - rect.left
    relativeY = event.clientY - rect.top
  }

  // Crear efecto ripple dentro del botón
  createRippleEffect(buttonElement, relativeX, relativeY)

  // Crear chispas solo si no está en modo reduced motion
  if (!prefersReducedMotion) {
    createMagicSparks(clickX, clickY)
  }

  // Efecto de pulso en el botón
  gsapContext.add(() => {
    gsap
      .timeline()
      .to(buttonElement, {
        duration: 0.1,
        scale: 0.95,
        ease: 'power2.out',
      })
      .to(buttonElement, {
        duration: 0.2,
        scale: 1.05,
        ease: 'power2.out',
      })
      .to(buttonElement, {
        duration: 0.1,
        scale: 1,
        ease: 'power2.out',
      })
  })
}

// Función para crear chispas mágicas (optimizada)
function createMagicSparks(clickX, clickY) {
  if (!sparkContainer) {
    sparkContainer = document.createElement('div')
    sparkContainer.className = 'fixed inset-0 pointer-events-none z-[9999]'
    document.body.appendChild(sparkContainer)
  }

  // Configuración de colores por variante
  const sparkConfigs = {
    primary: {
      gradient: 'linear-gradient(45deg, #4c00ff 0%, #ffffff 50%, #BFBFBF 100%)',
      boxShadow: '0 0 6px #4c00ff, 0 0 12px rgba(76, 0, 255, 0.5), 0 0 18px rgba(76, 0, 255, 0.3)',
    },
    secondary: {
      gradient: 'linear-gradient(45deg, #BFBFBF 0%, #ffffff 30%, #5A5A5A 70%, #2B2B2B 100%)',
      boxShadow:
        '0 0 6px #BFBFBF, 0 0 12px rgba(191, 191, 191, 0.6), 0 0 18px rgba(90, 90, 90, 0.4)',
    },
    light: {
      gradient: 'linear-gradient(45deg, #374151 0%, #ffffff 50%, #9ca3af 100%)',
      boxShadow:
        '0 0 6px #374151, 0 0 12px rgba(55, 65, 81, 0.4), 0 0 18px rgba(107, 114, 128, 0.3)',
    },
    flat: {
      gradient: 'linear-gradient(45deg, #4c00ff 0%, #ffffff 50%, #BFBFBF 100%)',
      boxShadow: '0 0 6px #4c00ff, 0 0 12px rgba(76, 0, 255, 0.5), 0 0 18px rgba(76, 0, 255, 0.3)',
    },
  }

  const sparkConfig = sparkConfigs[props.variant]
  const sparkCount = 12

  for (let i = 0; i < sparkCount; i++) {
    const spark = document.createElement('div')
    spark.className = 'magic-spark absolute w-1.5 h-1.5 rounded-full pointer-events-none'
    spark.style.background = sparkConfig.gradient
    spark.style.boxShadow = sparkConfig.boxShadow
    spark.style.left = clickX + 'px'
    spark.style.top = clickY + 'px'
    spark.style.transform = 'translate(-50%, -50%) scale(0)'
    spark.style.opacity = '0'

    sparkContainer.appendChild(spark)

    const angle = (i / sparkCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
    const distance = 50 + Math.random() * 100
    const endX = Math.cos(angle) * distance
    const endY = Math.sin(angle) * distance

    if (gsapContext) {
      gsapContext.add(() => {
        gsap
          .timeline({
            onComplete: () => {
              if (spark.parentNode) {
                spark.remove()
              }
            },
          })
          .to(spark, {
            duration: 0.1,
            scale: 1,
            opacity: 1,
            ease: 'power2.out',
          })
          .to(
            spark,
            {
              duration: 0.6,
              x: endX,
              y: endY,
              scale: 0.3,
              rotation: Math.random() * 360,
              ease: 'power2.out',
            },
            0
          )
          .to(
            spark,
            {
              duration: 0.3,
              opacity: 0,
              ease: 'power2.in',
            },
            0.3
          )
      })
    }
  }
}

// Función para crear efecto ripple dentro del botón (optimizada)
function createRippleEffect(buttonElement, x, y) {
  const ripple = document.createElement('div')
  ripple.className = 'magic-ripple absolute pointer-events-none z-20 rounded-full'
  ripple.style.mixBlendMode = 'screen'

  // Configuración de colores por variante
  const rippleConfigs = {
    primary: {
      background:
        'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 20%, rgba(76, 0, 255, 0.6) 40%, rgba(76, 0, 255, 0.3) 70%, transparent 100%)',
      boxShadow: '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(76, 0, 255, 0.4)',
    },
    secondary: {
      background:
        'radial-gradient(circle, rgba(191, 191, 191, 0.7) 0%, rgba(191, 191, 191, 0.5) 20%, rgba(90, 90, 90, 0.4) 40%, rgba(43, 43, 43, 0.3) 70%, transparent 100%)',
      boxShadow: '0 0 20px rgba(191, 191, 191, 0.6), 0 0 40px rgba(90, 90, 90, 0.4)',
    },
    light: {
      background:
        'radial-gradient(circle, rgba(55, 65, 81, 0.3) 0%, rgba(55, 65, 81, 0.2) 20%, rgba(107, 114, 128, 0.2) 40%, rgba(107, 114, 128, 0.1) 70%, transparent 100%)',
      boxShadow: '0 0 20px rgba(55, 65, 81, 0.4), 0 0 40px rgba(107, 114, 128, 0.2)',
    },
    flat: {
      background:
        'radial-gradient(circle, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.3) 20%, rgba(76, 0, 255, 0.2) 40%, transparent 100%)',
      boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
    },
  }

  const rippleConfig = rippleConfigs[props.variant]

  const size = Math.max(buttonElement.offsetWidth, buttonElement.offsetHeight) * 2.5
  ripple.style.width = size + 'px'
  ripple.style.height = size + 'px'
  ripple.style.background = rippleConfig.background
  ripple.style.boxShadow = rippleConfig.boxShadow
  ripple.style.left = x - size / 2 + 'px'
  ripple.style.top = y - size / 2 + 'px'
  ripple.style.transform = 'scale(0)'
  ripple.style.opacity = '0'

  buttonElement.appendChild(ripple)

  if (gsapContext) {
    gsapContext.add(() => {
      gsap
        .timeline({
          onComplete: () => {
            if (ripple.parentNode) {
              ripple.remove()
            }
          },
        })
        .to(ripple, {
          duration: 0.07,
          scale: 0.2,
          opacity: 1,
          ease: 'power1.out',
        })
        .to(ripple, {
          duration: 0.6,
          scale: 1,
          opacity: 0.9,
          ease: 'power2.out',
        })
        .to(
          ripple,
          {
            duration: 0.5,
            opacity: 0,
            scale: 1,
            ease: 'power3.out',
          },
          0.2
        )
    })
  }
}

onMounted(async () => {
  await nextTick()

  const button = buttonRef.value
  if (!button) return

  // Crear contexto GSAP para manejo de animaciones
  gsapContext = gsap.context(() => {}, button)

  // Aplicar shadow por defecto inicial
  if (gsapContext) {
    gsapContext.add(() => {
      gsap.set(button, {
        boxShadow: getDefaultShadow(),
      })
    })
  }
})

onBeforeUnmount(() => {
  // Limpiar todas las animaciones y event listeners
  if (gsapContext) {
    gsapContext.kill()
    gsapContext = null
  }

  // Limpiar contenedor de chispas
  if (sparkContainer && sparkContainer.parentNode) {
    sparkContainer.remove()
    sparkContainer = null
  }
})
</script>

<style scoped>
/* Efectos que no se pueden lograr con Tailwind CSS */
.magic-btn::before {
  content: '';
  @apply absolute inset-0 opacity-0 transition-all duration-300 ease-out -z-10 scale-95;
}

.magic-btn:hover::before {
  @apply opacity-100 scale-100;
}

.magic-btn--primary::before {
  background: linear-gradient(
    135deg,
    var(--color-ash) 0%,
    var(--color-electric-purple) 50%,
    #6d28d9 100%
  );
}

.magic-btn--secondary::before {
  background: linear-gradient(
    135deg,
    var(--color-ash) 0%,
    var(--color-steel) 50%,
    var(--color-charcoal) 100%
  );
}

.magic-btn--light::before {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #e2e8f0 100%);
}

.magic-btn--flat::before {
  background: var(--color-electric-purple);
}

.magic-btn:active {
  transform: translateY(-1px) scale(1.02);
  transition: all 0.1s ease;
}

/* Efectos de sparkle personalizados que requieren keyframes */
@keyframes sparkle {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.2) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.3) rotate(360deg);
    opacity: 0;
  }
}

:global(.magic-spark::before) {
  content: '';
  @apply absolute -inset-0.5;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%);
  animation: sparkle 0.6s ease-out;
}

/* Soporte para prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .magic-btn {
    @apply transition-none;
  }

  .magic-btn:hover {
    transform: none;
  }

  .btn-glow {
    @apply transition-none;
  }

  :global(.magic-spark),
  :global(.magic-ripple) {
    display: none;
  }
}
</style>
